var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { currentLocationPath } from './returnTo';
import { SignInError } from './SignInError';
class OidcUser {
    constructor(user) {
        this.user = user;
    }
    getProfileStringClaim(...claimNames) {
        for (const claimName of claimNames) {
            const value = this.user.profile[claimName];
            if (typeof value === 'string' && value) {
                return value;
            }
            if (Array.isArray(value)) {
                const firstString = value.find(item => typeof item === 'string' && item);
                if (typeof firstString === 'string') {
                    return firstString;
                }
            }
        }
    }
    // Same claim order and casing as both APIs (getEmail in torqagent-api, get_email in
    // torqcloud-api): email, preferred_username, upn, then emails[0], lower-cased. The UI's email
    // feeds the analytics identify and client-side checks such as the self-unshare check, so the
    // three layers have to agree on it.
    get attributes() {
        var _a;
        const email = (_a = this.getProfileStringClaim('email', 'preferred_username', 'upn', 'emails')) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        return email ? Object.assign(Object.assign({}, this.user.profile), { email }) : this.user.profile;
    }
    // The user id must match the one both APIs derive from the same token (cognito:username -> oid ->
    // sub), so ownership checks agree. Only stable claims: email, preferred_username and upn can
    // change or be reassigned, so they are never used as the id.
    get username() {
        var _a;
        return (_a = this.getProfileStringClaim('oid', 'sub')) !== null && _a !== void 0 ? _a : '';
    }
    get displayName() {
        var _a;
        return (_a = this.getProfileStringClaim('name', 'email', 'emails', 'preferred_username', 'sub')) !== null && _a !== void 0 ? _a : this.username;
    }
    get underlyingUserObject() {
        return this.user;
    }
}
class OidcSession {
    constructor(idToken) {
        this.idToken = idToken;
    }
    getIdToken() {
        return this.idToken;
    }
}
class OidcIdToken {
    constructor(jwt) {
        this.jwt = jwt;
        this.payload = jwtDecode(jwt);
    }
    getJwtToken() {
        return this.jwt;
    }
    getExpiration() {
        return this.payload['exp'];
    }
    getIssuedAt() {
        return this.payload['iat'];
    }
}
const TOKEN_EXPIRATION_LEEWAY_SECONDS = 30;
const RENEW_BEFORE_EXPIRATION_SECONDS = 300;
function isJwtUsable(jwt) {
    if (!jwt)
        return false;
    try {
        const payload = jwtDecode(jwt);
        if (!payload.exp)
            return false;
        return payload.exp * 1000 > Date.now() + TOKEN_EXPIRATION_LEEWAY_SECONDS * 1000;
    }
    catch (err) {
        console.warn('Unable to decode OIDC token', err);
        return false;
    }
}
/** The provider's error_description for a failed callback, from the URL or oidc-client-ts's ErrorResponse. */
function describeCallbackError(err) {
    var _a, _b, _c, _d;
    const params = new URLSearchParams(window.location.search);
    const response = err;
    const description = (_d = (_c = (_b = (_a = params.get('error_description')) !== null && _a !== void 0 ? _a : response === null || response === void 0 ? void 0 : response.error_description) !== null && _b !== void 0 ? _b : params.get('error')) !== null && _c !== void 0 ? _c : response === null || response === void 0 ? void 0 : response.error) !== null && _d !== void 0 ? _d : (err instanceof Error ? err.message : '');
    return description ? `Sign-in failed: ${description}` : 'Sign-in failed';
}
export class OidcAuthenticationManager {
    constructor(config) {
        var _a, _b;
        this.config = config;
        this.supportsPasswordSignIn = false;
        this.supportsPasswordChange = false;
        this.reauthenticatesOnSessionLoss = true;
        this.reauthInFlight = false;
        this.providerLabel = (_a = config.providerLabel) !== null && _a !== void 0 ? _a : 'Single Sign-On';
        this.userManager = new UserManager({
            authority: config.authority,
            client_id: config.client_id,
            scope: (_b = config.scope) !== null && _b !== void 0 ? _b : 'openid profile email offline_access',
            redirect_uri: config.redirect_uri,
            post_logout_redirect_uri: config.post_logout_redirect_uri,
            response_type: 'code',
            automaticSilentRenew: true,
            accessTokenExpiringNotificationTimeInSeconds: RENEW_BEFORE_EXPIRATION_SECONDS,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
        });
    }
    currentAuthenticatedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentValidUser();
            if (user) {
                return new OidcUser(user);
            }
        });
    }
    currentSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentValidUser();
            if (user === null || user === void 0 ? void 0 : user.id_token) {
                return new OidcSession(new OidcIdToken(user.id_token));
            }
            if (this.reauthInFlight) {
                // The session is gone and the redirect to the provider has started. Reject rather than
                // resolve undefined, so no caller (either axios client, either generated client) sends a
                // request without a token that the API would answer with a misleading 401.
                throw new axios.CanceledError('No valid session; re-authenticating');
            }
        });
    }
    signIn(_email, _password) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.startSignIn();
            throw new Error('Interactive sign-in was started');
        });
    }
    startSignIn(returnTo) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.signinRedirect({
                state: {
                    returnTo,
                },
            });
        });
    }
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.userManager.signoutRedirect();
            }
            catch (error) {
                yield this.userManager.removeUser();
                window.location.href = this.config.post_logout_redirect_uri;
            }
        });
    }
    changePassword(_user, _oldPassword, _newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('Password changes are managed by your identity provider');
        });
    }
    getCurrentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.isRedirectCallback()) {
                (_a = this.pendingCallback) !== null && _a !== void 0 ? _a : (this.pendingCallback = this.completeRedirectSignIn());
                return this.pendingCallback;
            }
            return (_b = (yield this.userManager.getUser())) !== null && _b !== void 0 ? _b : undefined;
        });
    }
    currentValidUser() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const user = yield this.getCurrentUser();
            if ((user === null || user === void 0 ? void 0 : user.id_token) && isJwtUsable(user.id_token)) {
                return user;
            }
            if (!user) {
                return undefined;
            }
            // One renewal at a time. Several callers with the same expired token (a tab waking up and
            // revalidating, for example) share it, so there is one refresh grant, and a failure in one
            // cannot remove a user a sibling just stored or start a second redirect.
            (_a = this.pendingRenewal) !== null && _a !== void 0 ? _a : (this.pendingRenewal = this.renewOrReauthenticate().finally(() => {
                this.pendingRenewal = undefined;
            }));
            return this.pendingRenewal;
        });
    }
    renewOrReauthenticate() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const renewedUser = yield this.userManager.signinSilent();
                if ((renewedUser === null || renewedUser === void 0 ? void 0 : renewedUser.id_token) && isJwtUsable(renewedUser.id_token)) {
                    return renewedUser;
                }
            }
            catch (err) {
                console.warn('Silent OIDC token renewal failed', err);
            }
            yield this.userManager.removeUser();
            // Renewal failed for a session that previously existed. Force interactive
            // re-authentication instead of returning undefined and letting the caller
            // fire an unauthenticated request (which the API rejects with a misleading
            // 401 and strands the user on a logged-out page). See GCC dead-session bug.
            yield this.forceReauthentication();
            return undefined;
        });
    }
    forceReauthentication() {
        return __awaiter(this, void 0, void 0, function* () {
            // Dedupe: many in-flight requests can hit this simultaneously, and we must
            // never trigger more than one redirect. Also skip while a redirect callback
            // is being processed (that path resolves to a valid user on its own).
            if (this.reauthInFlight || this.isRedirectCallback()) {
                return;
            }
            this.reauthInFlight = true;
            // Same-origin only: a location such as https://<app>//evil.example/x would otherwise be
            // stored and followed after the provider round trip.
            const returnTo = currentLocationPath();
            try {
                yield this.startSignIn(returnTo);
            }
            catch (err) {
                // Redirect could not be initiated; clear the guard so a later call retries
                // rather than deadlocking the app in a logged-out state.
                this.reauthInFlight = false;
                console.warn('Failed to start interactive re-authentication', err);
            }
        });
    }
    isRedirectCallback() {
        const searchParams = new URLSearchParams(window.location.search);
        return (searchParams.has('code') || searchParams.has('error')) && searchParams.has('state');
    }
    completeRedirectSignIn() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const user = yield this.userManager.signinRedirectCallback(window.location.href);
                const callbackTarget = this.resolveCallbackTarget(user);
                if (callbackTarget) {
                    window.history.replaceState({}, document.title, callbackTarget);
                }
                return user;
            }
            catch (err) {
                // The provider refused (?error=access_denied&state=...) or the state did not match. The
                // state is consumed either way, so strip the callback params before rethrowing: left in
                // place, the next currentAuthenticatedUser() would run the callback again and fail with
                // "No matching state found" instead of the provider's own description.
                const message = describeCallbackError(err);
                window.history.replaceState({}, document.title, this.resolveCallbackTarget());
                throw new SignInError(message);
            }
            finally {
                this.pendingCallback = undefined;
            }
        });
    }
    resolveCallbackTarget(user) {
        var _a;
        const requestedReturnTo = typeof (user === null || user === void 0 ? void 0 : user.state) === 'object' && user.state && 'returnTo' in user.state ? String(user.state.returnTo || '') : '';
        if (requestedReturnTo) {
            try {
                const target = new URL(requestedReturnTo, window.location.origin);
                if (target.origin === window.location.origin) {
                    return `${target.pathname}${target.search}${target.hash}`;
                }
                // Another origin is followed only when the app lists it, the same check Login applies
                // to a returnTo query parameter. Anything else falls through to the current URL.
                if ((_a = this.config.allowedReturnOrigins) === null || _a === void 0 ? void 0 : _a.includes(target.origin)) {
                    window.location.replace(target.toString());
                    return undefined;
                }
            }
            catch (_b) {
                // Ignore malformed callback targets and fall through to the sanitized current URL.
            }
        }
        const url = new URL(window.location.href);
        for (const param of ['code', 'state', 'session_state', 'iss', 'error', 'error_description']) {
            url.searchParams.delete(param);
        }
        return `${url.pathname}${url.search}${url.hash}`;
    }
}
//# sourceMappingURL=Oidc.js.map